import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import fetchApi from '../../utils/api';
import html2pdf from 'html2pdf.js';
import AirDatePicker from '../ui/AirDatePicker';

const TRANS_MODES = [
    { value: '1', label: 'Road' },
    { value: '2', label: 'Rail' },
    { value: '3', label: 'Air' },
    { value: '4', label: 'Ship' },
];
const VEHICLE_TYPES = [
    { value: 'R', label: 'Regular' },
    { value: 'O', label: 'Over Dimensional Cargo' },
];
const CANCEL_REASONS = [
    { value: '1', label: 'Duplicate' },
    { value: '2', label: 'Order Cancelled' },
    { value: '3', label: 'Data Entry Mistake' },
    { value: '4', label: 'Others' },
];
const EXTEND_REASONS = [
    { value: '1', label: 'Natural Calamity' },
    { value: '2', label: 'Law and Order Situation' },
    { value: '3', label: 'Transhipment' },
    { value: '4', label: 'Accident' },
    { value: '5', label: 'Others' },
];

const STATUS_COLORS = {
    GENERATED: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    CANCELLED: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    CLOSED: { bg: '#f3f4f6', color: '#1f2937', border: '#d1d5db' },
    EXPIRED: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
    PENDING: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

const panelStyle = {
    width: '300px',
    minWidth: '280px',
    background: '#fff',
    borderLeft: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    height: '100%',
    overflowY: 'auto',
    fontSize: '13px',
    fontFamily: 'Inter, Arial, sans-serif',
};

const sectionStyle = {
    borderBottom: '1px solid #e2e8f0',
    padding: '14px 16px',
};

const labelStyle = { fontWeight: 600, color: '#374151', fontSize: '12px', marginBottom: '2px' };
const valueStyle = { color: '#1e293b', fontWeight: 500 };
const inputStyle = {
    width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1',
    borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
    background: '#f8fafc', outline: 'none', marginTop: '2px'
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };
const btnPrimary = {
    width: '100%', padding: '9px', background: '#1e3a5f', color: '#fff',
    border: 'none', borderRadius: '7px', fontWeight: 600, fontSize: '13px',
    cursor: 'pointer', marginTop: '8px', letterSpacing: '0.3px'
};
const btnSecondary = {
    width: '100%', padding: '8px', background: '#f1f5f9', color: '#374151',
    border: '1px solid #cbd5e1', borderRadius: '7px', fontWeight: 500,
    fontSize: '13px', cursor: 'pointer', marginTop: '6px'
};
const btnDanger = { ...btnSecondary, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' };

export default function EWayBillPanel({ invoiceData, onRefresh }) {
    const invoiceId = invoiceData?._id || invoiceData?.id;
    const isClosed = invoiceData?.ewb_closure_status === 'CLOSED' || invoiceData?.ewb_status === 'CLOSED';
    const isCancelled = !isClosed && (invoiceData?.ewb_status === 'CANCELLED' || invoiceData?.ewb_cancel_status === 'CANCELLED');
    const ewbStatus = isClosed ? 'CLOSED' : (isCancelled ? 'PENDING' : (invoiceData?.ewb_status || 'PENDING'));
    const ewbNo = isCancelled ? '' : (invoiceData?.e_way_bill_no || '');
    const ewbDate = isCancelled ? '' : (invoiceData?.eway_bill_date || '');
    const validUpto = isCancelled ? '' : (invoiceData?.valid_upto || '');
    const cancelDate = invoiceData?.cancel_date || '';
    const cancelRmrk = invoiceData?.cancel_rmrk || '';

    // Generate form state
    const [genForm, setGenForm] = useState({
        transMode: '1', vehicleType: 'R', vehicleNo: invoiceData?.vehicle_no || '',
        transDistance: '', qtyUnit: 'NOS', confirmed: false,
        fromPlace: '', transporterId: '', transporterName: '',
    });
    const [loading, setLoading] = useState(false);

    // Cancel form
    const [showCancel, setShowCancel] = useState(false);
    const [cancelForm, setCancelForm] = useState({ cancelRsnCode: '1', cancelRmrk: '' });

    // Close form
    const [showClose, setShowClose] = useState(false);
    const [closeForm, setCloseForm] = useState({ remarks: '', closureDate: new Date().toISOString().split('T')[0] });

    // Update vehicle form
    const [showUpdateVeh, setShowUpdateVeh] = useState(false);
    const [vehForm, setVehForm] = useState({
        vehicleNo: '', fromPlace: '', fromState: '',
        reasonCode: '1', reasonRem: '', transMode: '1'
    });

    // Update transporter form
    const [showUpdateTrans, setShowUpdateTrans] = useState(false);
    const [transForm, setTransForm] = useState({ transporterId: '' });

    // Extend validity form
    const [showExtend, setShowExtend] = useState(false);
    const [extForm, setExtForm] = useState({
        vehicleNo: ewbNo ? (invoiceData?.vehicle_no || '') : '',
        fromPlace: '', fromState: '', frompincode: '',
        remainingDistance: '', extnRsnCode: '4', extnRemarks: '',
    });

    // ── Generate EWB ──────────────────────────────────────────────────────────
    const handleGenerate = async () => {
        if (!genForm.confirmed) { toast.warn('Please confirm before generating E-Way Bill'); return; }
        if (!invoiceId) { toast.error('Invoice ID missing'); return; }
        setLoading(true);
        try {
            const res = await fetchApi(`/ewb/generate/${invoiceId}`, {
                method: 'POST',
                body: JSON.stringify({
                    transMode: genForm.transMode,
                    vehicleType: genForm.vehicleType,
                    vehicleNo: genForm.vehicleNo,
                    transDistance: genForm.transDistance || '0',
                    qtyUnit: genForm.qtyUnit,
                    fromPlace: genForm.fromPlace,
                    transporterId: genForm.transporterId,
                    transporterName: genForm.transporterName,
                })
            });
            toast.success(`E-Way Bill generated: ${res.ewbNo}`);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err.message || 'Failed to generate E-Way Bill');
        } finally {
            setLoading(false);
        }
    };

    // ── Cancel EWB ────────────────────────────────────────────────────────────
    const handleCancel = async () => {
        if (!cancelForm.cancelRmrk) { toast.warn('Remarks are required'); return; }
        const conf = await Swal.fire({ title: 'Cancel E-Way Bill?', text: `EWB No: ${ewbNo} will be cancelled.`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#991b1b', confirmButtonText: 'Yes, Cancel' });
        if (!conf.isConfirmed) return;
        setLoading(true);
        try {
            await fetchApi(`/ewb/cancel/${invoiceId}`, { method: 'POST', body: JSON.stringify(cancelForm) });
            toast.success('E-Way Bill cancelled');
            setShowCancel(false);
            if (onRefresh) onRefresh();
        } catch (err) { toast.error(err.message || 'Cancellation failed'); }
        finally { setLoading(false); }
    };

    // ── Close EWB ────────────────────────────────────────────────────────────
    const handleCloseEwb = async () => {
        if (!closeForm.remarks) { toast.warn('Remarks are required'); return; }
        if (!closeForm.closureDate) { toast.warn('Closure Date is required'); return; }
        const conf = await Swal.fire({ title: 'Close E-Way Bill?', text: `EWB No: ${ewbNo} will be closed.`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#1e3a5f', confirmButtonText: 'Yes, Close' });
        if (!conf.isConfirmed) return;
        setLoading(true);
        try {
            await fetchApi(`/ewb/close/${invoiceId}`, { method: 'POST', body: JSON.stringify(closeForm) });
            toast.success('E-Way Bill closed successfully');
            setShowClose(false);
            if (onRefresh) onRefresh();
        } catch (err) { toast.error(err.message || 'Closure failed'); }
        finally { setLoading(false); }
    };

    // ── Update Vehicle ────────────────────────────────────────────────────────
    const handleUpdateVehicle = async () => {
        if (!vehForm.vehicleNo || !vehForm.fromPlace || !vehForm.fromState) { toast.warn('Vehicle No, From Place and State are required'); return; }
        setLoading(true);
        try {
            await fetchApi(`/ewb/update-vehicle/${invoiceId}`, { method: 'POST', body: JSON.stringify(vehForm) });
            toast.success('Vehicle number updated');
            setShowUpdateVeh(false);
            if (onRefresh) onRefresh();
        } catch (err) { toast.error(err.message || 'Update failed'); }
        finally { setLoading(false); }
    };

    // ── Update Transporter ────────────────────────────────────────────────────
    const handleUpdateTransporter = async () => {
        if (!transForm.transporterId) { toast.warn('Transporter GSTIN is required'); return; }
        setLoading(true);
        try {
            await fetchApi(`/ewb/update-transporter/${invoiceId}`, { method: 'POST', body: JSON.stringify(transForm) });
            toast.success('Transporter updated');
            setShowUpdateTrans(false);
            if (onRefresh) onRefresh();
        } catch (err) { toast.error(err.message || 'Update failed'); }
        finally { setLoading(false); }
    };

    // ── Extend Validity ───────────────────────────────────────────────────────
    const handleExtendValidity = async () => {
        if (!extForm.fromPlace || !extForm.fromState) { toast.warn('From Place and State are required'); return; }
        setLoading(true);
        try {
            await fetchApi(`/ewb/extend-validity/${invoiceId}`, { method: 'POST', body: JSON.stringify(extForm) });
            toast.success('Validity extended');
            setShowExtend(false);
            if (onRefresh) onRefresh();
        } catch (err) { toast.error(err.message || 'Extension failed'); }
        finally { setLoading(false); }
    };

    // ── Download PDF ──────────────────────────────────────────────────────────
    const pdfRef = useRef(null);
    const handleDownloadPdf = async () => {
        if (!ewbNo) return;
        try {
            setLoading(true);
            toast.info('Generating E-Way Bill PDF...');

            // Try to get fresh details
            let ewbDetails = null;
            try {
                const res = await fetchApi(`/ewb/details/${invoiceId}`);
                ewbDetails = res.data;
            } catch (e) {
                console.warn('Could not fetch EWB details, using basic info for PDF', e);
            }

            const ewbData = ewbDetails || {};

            const docDate = ewbData.docDate || invoiceData?.invoice_date || '';
            const ewbNumber = ewbData.ewayBillNo || ewbNo || '';
            const ewbDateStr = ewbData.ewayBillDate || ewbDate || '';
            const validUptoStr = ewbData.validUpto || validUpto || '';

            const generatedBy = ewbData.fromGstin || '';
            const distance = ewbData.actualDist || invoiceData?.transDistance || '0';
            const typeText = 'Outward - Supply';
            const docDetails = `Tax invoice - ${ewbData.docNo || invoiceData?.invoice_no || ''} - ${docDate}`;
            const transType = '1';
            const portal = '1';

            const fromGstin = ewbData.fromGstin || '';
            const fromName = ewbData.fromTrdName || '';
            const fromState = ewbData.fromStateCode || '';
            const fromAddress = `${ewbData.fromAddr1 || ''} ${ewbData.fromAddr2 || ''}`;
            const fromCityPin = `${ewbData.fromPlace || ''}-${ewbData.fromPincode || ''}`;

            const toGstin = ewbData.toGstin || 'URP';
            const toName = ewbData.toTrdName || '';
            const toState = ewbData.toStateCode || '';
            const toAddress = `${ewbData.toAddr1 || ''} ${ewbData.toAddr2 || ''}`;
            const toCityPin = `${ewbData.toPlace || ''}-${ewbData.toPincode || ''}`;

            let itemList = ewbData.itemList ? [...ewbData.itemList] : [];

            // Retrofit missing Extra Charges & Insurance for Old EWBs in the PDF
            const extraAmt = parseFloat(invoiceData?.extr_charges_amount || 0);
            let subTotal = 0;
            try {
                const invItems = typeof invoiceData?.items === 'string' ? JSON.parse(invoiceData.items) : (invoiceData?.items || []);
                subTotal = invItems.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
            } catch (e) {}
            const insuranceRate = parseFloat(invoiceData?.insurance || 0);
            const insuranceAmt = (subTotal * insuranceRate) / 100;
            
            const tax1Rate = parseFloat(invoiceData?.sale_type_tax1 || 0);
            const tax2Rate = parseFloat(invoiceData?.sale_type_tax2 || 0);
            const isInterState = tax2Rate === 0;

            if (extraAmt > 0) {
                const extraName = invoiceData?.extra_charges_name || 'Extra Charges';
                const hasExtra = itemList.some(it => (it.productName || '').toUpperCase().includes(extraName.toUpperCase().substring(0, 10)));
                if (!hasExtra) {
                    let hsnCode = 999732;

                    itemList.push({
                        hsnCode,
                        productName: extraName.substring(0, 100),
                        quantity: 1,
                        qtyUnit: 'BOX',
                        taxableAmount: extraAmt.toFixed(2),
                        cgstRate: isInterState ? 0 : tax1Rate,
                        sgstRate: isInterState ? 0 : tax2Rate,
                        igstRate: isInterState ? (tax1Rate + tax2Rate) : 0
                    });
                }
            }

            if (insuranceAmt > 0) {
                const hasIns = itemList.some(it => (it.productName || '').toUpperCase().includes('INSURANCE'));
                if (!hasIns) {
                    itemList.push({
                        hsnCode: 997136,
                        productName: 'INSURANCE CHARGES',
                        quantity: 1,
                        qtyUnit: 'BOX',
                        taxableAmount: insuranceAmt.toFixed(2),
                        cgstRate: isInterState ? 0 : tax1Rate,
                        sgstRate: isInterState ? 0 : tax2Rate,
                        igstRate: isInterState ? (tax1Rate + tax2Rate) : 0
                    });
                }
            }

            // Retrofit discount proportional mapping for Old EWBs in the PDF
            const discountVal = parseFloat(invoiceData?.discount || 0);
            const discountAmt = (invoiceData?.discount_type === '%') ? (subTotal * discountVal) / 100 : discountVal;
            if (discountAmt > 0 && subTotal > 0) {
                let originalSum = 0;
                itemList.forEach(it => {
                    const name = (it.productName || '').toUpperCase();
                    if (!name.includes('EXTRA') && !name.includes('INSURANCE') && !name.includes('COURIER') && !name.includes('FREIGHT')) {
                        originalSum += parseFloat(it.taxableAmount) || 0;
                    }
                });

                if (Math.abs(originalSum - subTotal) < 1.0) {
                    itemList = itemList.map(it => {
                        const name = (it.productName || '').toUpperCase();
                        if (!name.includes('EXTRA') && !name.includes('INSURANCE') && !name.includes('COURIER') && !name.includes('FREIGHT')) {
                            let itemTaxable = parseFloat(it.taxableAmount) || 0;
                            const proportion = itemTaxable / subTotal;
                            itemTaxable = itemTaxable - (discountAmt * proportion);
                            return { ...it, taxableAmount: itemTaxable.toFixed(2) };
                        }
                        return it;
                    });
                }
            }

            const itemListHtml = itemList.length > 0 ? itemList.map(item => {
                const name = (item.productName || '').toUpperCase();
                const isExtraOrInsurance = name.includes('EXTRA') || name.includes('INSURANCE') || name.includes('COURIER') || name.includes('FREIGHT');
                const qtyDisplay = isExtraOrInsurance ? 'Other' : `${item.quantity || '0'} ${item.qtyUnit || ''}`;

                return `
                <tr>
                    <td style="border: 1px solid #000; padding: 4px;">${item.hsnCode || '-'}</td>
                    <td style="border: 1px solid #000; padding: 4px;">${item.productName || '-'}</td>
                    <td style="border: 1px solid #000; padding: 4px;">${qtyDisplay}</td>
                    <td style="border: 1px solid #000; padding: 4px;">${item.taxableAmount || '0.00'}</td>
                    <td style="border: 1px solid #000; padding: 4px;">${((parseFloat(item.cgstRate) || 0) + (parseFloat(item.sgstRate) || 0) + (parseFloat(item.igstRate) || 0)).toFixed(2)}+0.00+0.00+0.00</td>
                </tr>
            `}).join('') : `
                <tr>
                    <td style="border: 1px solid #000; padding: 4px;">-</td>
                    <td style="border: 1px solid #000; padding: 4px;">-</td>
                    <td style="border: 1px solid #000; padding: 4px;">-</td>
                    <td style="border: 1px solid #000; padding: 4px;">-</td>
                    <td style="border: 1px solid #000; padding: 4px;">-</td>
                </tr>
            `;

            const vehicleList = ewbData.VehiclListDetails || ewbData.vehclList || [];
            const vehicle = vehicleList.length > 0 ? vehicleList[0] : {};
            const vehNo = vehicle.vehicleNo || invoiceData?.vehicle_no || '-';
            const vehMode = vehicle.transMode === '1' ? 'Road' : (vehicle.transMode === '2' ? 'Rail' : (vehicle.transMode === '3' ? 'Air' : (vehicle.transMode === '4' ? 'Ship' : 'Road')));
            const vehDocNoDate = `${vehicle.transDocNo || '-'} & ${vehicle.transDocDate || '-'}`;
            const vehEnteredDate = vehicle.enteredDate || ewbDateStr;
            const vehEnteredBy = vehicle.userGstin || fromGstin;

            const qrData = `${ewbNumber}\n${generatedBy}\n${ewbDateStr}`;
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`;
            const barcodeUrl = `https://bwipjs-api.metafloor.com/?bcid=code128&text=${ewbNumber}&scaleX=2&scaleY=1`;

            const element = document.createElement('div');
            element.innerHTML = `
                <div style="padding: 15px; font-family: Arial, sans-serif; color: #000; font-size: 11px; position: relative;">
                    <!-- HEADER -->
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px;">
                        <h2 style="margin: 0; font-size: 18px; font-weight: bold;">e-Way Bill</h2>
                        <div>
                            <img src="${qrUrl}" alt="QR Code" style="width: 70px; height: 70px; border: 1px solid #000;" crossorigin="anonymous"/>
                        </div>
                    </div>

                    <style>
                        .ewb-table { width: 100%; border-collapse: collapse; border: 1px solid #000; margin-bottom: 10px; }
                        .ewb-table th, .ewb-table td { border: 1px solid #000; padding: 4px 6px; vertical-align: top; }
                        .ewb-header { background-color: #6b66ad; color: white; font-weight: bold; font-size: 11px; padding: 4px 6px; border: 1px solid #000; border-bottom: none; }
                        .ewb-strong { font-weight: bold; }
                    </style>

                    <!-- 1. E-WAY BILL Details -->
                    <div class="ewb-header">1. E-WAY BILL Details</div>
                    <table class="ewb-table">
                        <tr>
                            <td style="width: 33%;">eWay Bill No: <span class="ewb-strong">${ewbNumber}</span></td>
                            <td style="width: 33%;">Generated Date: <span class="ewb-strong">${ewbDateStr}</span></td>
                            <td style="width: 34%;">Generated By: <span class="ewb-strong">${generatedBy}</span><br/>Valid Upto: <span class="ewb-strong">${validUptoStr}</span></td>
                        </tr>
                        <tr>
                            <td>Mode: <span class="ewb-strong">${vehMode}</span></td>
                            <td>Approx Distance: <span class="ewb-strong">${distance} km</span></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Type: <span class="ewb-strong">${typeText}</span></td>
                            <td>Document Details: <span class="ewb-strong">${docDetails}</span></td>
                            <td>Transaction type: <span class="ewb-strong">${transType}</span><br/>Portal: <span class="ewb-strong">${portal}</span></td>
                        </tr>
                    </table>

                    <!-- 2. Address Details -->
                    <div class="ewb-header">2. Address Details</div>
                    <table class="ewb-table">
                        <tr>
                            <td style="width: 50%; background-color: #d8d6eb; font-weight: bold;">From</td>
                            <td style="width: 50%; background-color: #d8d6eb; font-weight: bold;">To</td>
                        </tr>
                        <tr>
                            <td>
                                GSTIN : ${fromGstin}<br/>
                                ${fromName}<br/>
                                ${fromState}<br/>
                                :: Dispatch From ::<br/>
                                ${fromAddress}<br/>
                                ${fromCityPin}
                            </td>
                            <td>
                                GSTIN : ${toGstin}<br/>
                                ${toName}<br/>
                                ${toState}<br/>
                                :: Ship To ::<br/>
                                ${toAddress}<br/>
                                ${toCityPin}
                            </td>
                        </tr>
                    </table>

                    <!-- 3. Goods Details -->
                    <div class="ewb-header">3. Goods Details</div>
                    <table class="ewb-table">
                        <tr style="background-color: #f0f0f0;">
                            <th>HSN Code</th>
                            <th>Product Name & Desc.</th>
                            <th>Quantity</th>
                            <th>Taxable<br/>Amount Rs.</th>
                            <th>Tax Rate<br/>(C+S+I+Cess+Cess<br/>Non.Advol.)</th>
                        </tr>
                        ${itemListHtml}
                    </table>
                    <table class="ewb-table" style="border-top: none; margin-top: -11px;">
                        <tr style="background-color: #f0f0f0;">
                            <th>Tot. Tax'ble Amt</th>
                            <th>CGST Amt</th>
                            <th>SGST Amt</th>
                            <th>IGST Amt</th>
                            <th>CESS Amt</th>
                            <th>CESS Non.Advol Amt</th>
                            <th>Other Amt</th>
                            <th>Total Inv.Amt</th>
                        </tr>
                        <tr>
                            <td>${ewbData.totalValue || '0.00'}</td>
                            <td>${ewbData.cgstValue || '0.00'}</td>
                            <td>${ewbData.sgstValue || '0.00'}</td>
                            <td>${ewbData.igstValue || '0.00'}</td>
                            <td>${ewbData.cessValue || '0.00'}</td>
                            <td>${ewbData.cessNonAdvolValue || '0.00'}</td>
                            <td>${ewbData.otherValue || '0.00'}</td>
                            <td>${ewbData.totInvValue || '0.00'}</td>
                        </tr>
                    </table>

                    <!-- 4. Transportation Details -->
                    <div class="ewb-header">4. Transportation Details</div>
                    <table class="ewb-table">
                        <tr>
                            <td style="width: 50%;">Transporter ID & Name : <span class="ewb-strong">${ewbData.transporterId || ''} ${ewbData.transporterName || ''}</span></td>
                            <td style="width: 50%;">Transport Doc. No & Date : <span class="ewb-strong">${vehDocNoDate}</span></td>
                        </tr>
                    </table>

                    <!-- 5. Vehicle Details -->
                    <div class="ewb-header">5. Vehicle Details</div>
                    <table class="ewb-table">
                        <tr style="background-color: #f0f0f0;">
                            <th>Mode</th>
                            <th>Vehicle / Trans Doc No & Dt.</th>
                            <th>From</th>
                            <th>Entered Date</th>
                            <th>Entered By</th>
                            <th>CEWB No. (If any)</th>
                            <th>Multi Veh.Info (If any)</th>
                            <th>Portal</th>
                        </tr>
                        <tr>
                            <td>${vehMode}</td>
                            <td>${vehNo}</td>
                            <td>${ewbData.fromPlace || '-'}</td>
                            <td>${vehEnteredDate}</td>
                            <td>${vehEnteredBy}</td>
                            <td>-</td>
                            <td>-</td>
                            <td>${portal}</td>
                        </tr>
                    </table>

                    <div style="text-align: center; margin-top: 15px;">
                        <img src="${barcodeUrl}" alt="Barcode" style="height: 50px;" crossorigin="anonymous"/>
                        <div style="font-size: 10px; margin-top: 2px;">${ewbNumber}</div>
                    </div>

                    ${ewbStatus === 'CANCELLED' ? '<div style="color: red; font-size: 40px; font-weight: bold; text-align: center; position: absolute; top: 40%; left: 30%; transform: rotate(-30deg); border: 4px solid red; padding: 10px; background: rgba(255,255,255,0.8);">CANCELLED</div>' : ''}
                </div>
            `;

            const opt = {
                margin: 5,
                filename: `EWB_${ewbNo}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            await html2pdf().set(opt).from(element).save();
            toast.success('PDF Downloaded successfully');
        } catch (err) {
            console.error('PDF Generation Error:', err);
            toast.error('Failed to generate PDF');
        } finally {
            setLoading(false);
        }
    };

    const statusStyle = STATUS_COLORS[ewbStatus] || STATUS_COLORS.PENDING;

    return (
        <div style={panelStyle}>
            {/* ── HEADER & STATUS ─────────────────────── */}
            <div style={{ padding: '16px 16px 8px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 700, color: '#4b5563' }}>E-Way Bill</span>
                    <button
                        style={{ background: '#00334e', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => window.open('https://ewaybillgst.gov.in/Others/P2PDistance.aspx', '_blank')}
                    >
                        Get Distance
                    </button>
                </div>

                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                    <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>Status:</span> <span style={{ color: '#6b7280' }}>{ewbStatus === 'PENDING' ? 'Pending' : (ewbStatus === 'CLOSED' ? 'Closed' : ewbStatus)}</span></div>
                    <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>EWB No:</span> <span style={{ color: '#6b7280' }}>{ewbNo}</span></div>
                    <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>EWB Date:</span> <span style={{ color: '#6b7280' }}>{ewbDate}</span></div>
                    {isClosed && invoiceData?.ewb_closure_date && (
                        <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>Close Date:</span> <span style={{ color: '#6b7280' }}>{invoiceData.ewb_closure_date}</span></div>
                    )}
                    <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>Valid Till:</span> <span style={{ color: '#6b7280' }}>{validUpto}</span></div>
                    <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>Cancel Status:</span> <span style={{ color: '#6b7280' }}>{isCancelled ? 'Cancelled' : ''}</span></div>
                    <div style={{ display: 'flex' }}><span style={{ fontWeight: 700, color: '#374151', width: '120px' }}>Message:</span> <span style={{ color: '#6b7280' }}>{isCancelled ? cancelRmrk : (isClosed ? (invoiceData?.ewb_closure_remarks || cancelRmrk) : '')}</span></div>
                </div>
            </div>

            {/* ── GENERATE FORM ─── */}
            {ewbStatus === 'PENDING' && (
                <div style={{ margin: '16px', padding: '16px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#6b7280', marginBottom: '16px' }}>Generate E-Way Bill</div>

                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '12px', marginBottom: '4px' }}>Distance (KM)</div>
                        <input style={{ ...inputStyle, height: '36px' }} type="number" min="0" placeholder="0"
                            value={genForm.transDistance}
                            onChange={e => setGenForm(p => ({ ...p, transDistance: e.target.value }))} />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '12px', marginBottom: '4px' }}>Transport Mode</div>
                        <select style={{ ...selectStyle, height: '36px' }} value={genForm.transMode} onChange={e => setGenForm(p => ({ ...p, transMode: e.target.value }))}>
                            {TRANS_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '12px', marginBottom: '4px' }}>Vehicle Type</div>
                        <select style={{ ...selectStyle, height: '36px' }} value={genForm.vehicleType} onChange={e => setGenForm(p => ({ ...p, vehicleType: e.target.value }))}>
                            {VEHICLE_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '12px', marginBottom: '4px' }}>Vehicle No</div>
                        <input style={{ ...inputStyle, height: '36px' }} placeholder="e.g. GJ03CC1234" value={genForm.vehicleNo}
                            onChange={e => setGenForm(p => ({ ...p, vehicleNo: e.target.value.toUpperCase() }))} />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '12px', marginBottom: '4px' }}>Qty Unit Code</div>
                        <select style={{ ...selectStyle, height: '36px' }} value={genForm.qtyUnit} onChange={e => setGenForm(p => ({ ...p, qtyUnit: e.target.value }))}>
                            {['NOS', 'BOX', 'KGS', 'MTR', 'LTR', 'PCS', 'SET', 'TON', 'BAG', 'ROL'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', marginBottom: '16px' }}>
                        <input type="checkbox" checked={genForm.confirmed} onChange={e => setGenForm(p => ({ ...p, confirmed: e.target.checked }))}
                            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1e3a5f' }} />
                        <span style={{ fontSize: '13px', color: '#111827', fontWeight: 700 }}>Confirm Generate E-Way Bill</span>
                    </label>

                    <button
                        style={{
                            width: '100%', padding: '10px',
                            background: genForm.confirmed ? '#00334e' : '#9ca3af',
                            color: '#fff', border: 'none', borderRadius: '6px',
                            fontWeight: 700, fontSize: '14px',
                            cursor: genForm.confirmed ? 'pointer' : 'not-allowed'
                        }}
                        onClick={handleGenerate} disabled={loading || !genForm.confirmed}>
                        {loading ? 'Generating...' : 'Generate EWB'}
                    </button>
                </div>
            )}

            {/* ── BOTTOM BUTTONS ─── */}
            {(ewbStatus === 'GENERATED' || ewbStatus === 'CLOSED') && (
                <div style={{ padding: '0 16px 16px 16px', marginTop: 'auto' }}>
                    <button
                        style={{
                            width: '100%', padding: '10px',
                            background: '#fff', color: '#475569',
                            border: '1px solid #94a3b8',
                            borderRadius: '6px', fontWeight: 600, fontSize: '14px',
                            cursor: 'pointer'
                        }}
                        onClick={handleDownloadPdf} disabled={loading}>
                        EWB PDF
                    </button>
                </div>
            )}

            {/* Extended Action Options when Generated */}
            {ewbStatus === 'GENERATED' && (
                <div style={{ padding: '0 16px 16px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={{ ...btnSecondary, fontSize: '12px', padding: '6px', flex: 1 }} onClick={() => { setShowCancel(v => !v); setShowClose(false); }}>Cancel EWB</button>
                        <button style={{ ...btnSecondary, fontSize: '12px', padding: '6px', flex: 1 }} onClick={() => { setShowClose(v => !v); setShowCancel(false); }}>Close EWB</button>
                    </div>
                    {showCancel && (
                        <div style={{ background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '7px', padding: '10px', marginTop: '6px' }}>
                            <div style={labelStyle}>Cancel Reason</div>
                            <select style={selectStyle} value={cancelForm.cancelRsnCode} onChange={e => setCancelForm(p => ({ ...p, cancelRsnCode: e.target.value }))}>
                                {CANCEL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <div style={{ ...labelStyle, marginTop: '6px' }}>Remarks</div>
                            <input style={inputStyle} value={cancelForm.cancelRmrk} onChange={e => setCancelForm(p => ({ ...p, cancelRmrk: e.target.value }))} placeholder="Enter remarks..." />
                            <button style={{ ...btnDanger, marginTop: '8px', opacity: loading ? 0.6 : 1 }} onClick={handleCancel} disabled={loading}>{loading ? 'Cancelling...' : 'Confirm Cancel'}</button>
                        </div>
                    )}
                    {showClose && (
                        <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '7px', padding: '10px', marginTop: '6px' }}>
                            <div style={labelStyle}>Closure Date</div>
                            <AirDatePicker
                                style={inputStyle}
                                value={closeForm.closureDate}
                                onChange={(dateStr) => setCloseForm(p => ({ ...p, closureDate: dateStr }))}
                            />
                            <div style={{ ...labelStyle, marginTop: '6px' }}>Closure Remarks</div>
                            <input style={inputStyle} value={closeForm.remarks} onChange={e => setCloseForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Enter remarks (e.g. Closed the order)..." />
                            <button style={{ ...btnPrimary, marginTop: '8px', opacity: loading ? 0.6 : 1 }} onClick={handleCloseEwb} disabled={loading}>{loading ? 'Closing...' : 'Confirm Close'}</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
